<script>
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import TabTrapHelper from '../../../../lib/helpers/TabTrapHelper';
    import {
        idForDay,
        isSameDate,
        dateFromString,
        dateToString,
    } from '../../DatePicker/v1/dateHelpers';
    import TextField from '../../TextField/v1/TextField.svelte';
    import DatePicker from '../../DatePicker/v1/DatePicker.svelte';
    import { createEventDispatcher, onMount } from 'svelte';

    export let autocomplete = 'off';
    export let closeOnSelect = true;
    export let disabled = false;
    export let expanded = false;
    export let firstDayOfWeek = 1;
    export let format = 'MM.DD.YYYY';
    export let highlightedDates = [];
    export let i18n = {};
    export let id = 'date-picker';
    export let inputValue = '';
    export let label = undefined;
    export let labelHidden = false;
    /** @type {'left' | 'top'} */
    export let labelPosition = 'top';
    export let locale = 'en-US';
    export let placeholder = '';
    export let hasError = false;
    export let errorMessage = undefined;
    export let value = null;

    let position = { x: 0, y: 0 };
    let wrapperElement;
    let inputElement;
    let tabTrapElement;
    let selectorStartDate;
    let tabTrapper;

    const dispatch = createEventDispatcher();

    onMount(() => {
        tabTrapper = new TabTrapHelper(tabTrapElement);
    });

    $: valueChanged(value);

    function valueChanged(value) {
        const newInputValue = dateToString(value, format);

        if (inputValue !== newInputValue) {
            inputValue = newInputValue;
        }
    }

    $: inputValueChanged(inputValue);

    function inputValueChanged(inputValue) {
        const newValue = dateFromString(inputValue, format);

        if (newValue !== null) {
            if (!isSameDate(value, newValue)) {
                selectorStartDate = newValue;
            }
        } else {
            selectorStartDate = new Date();
        }

        if (value !== newValue) {
            value = newValue;
        }
    }

    $: if (expanded) {
        setTimeout(() => {
            tabTrapper.init();
        }, 0);
    }

    $: style = `left: ${position.x}px; top: ${position.y}px;`;

    function focusValue() {
        if (!value) {
            value = new Date();
        }

        const selector = `.prom-date-picker__${idForDay(value)}`;

        setTimeout(() => {
            if (expanded) {
                tabTrapElement.querySelector(selector).focus();
            }
        }, 0);
    }

    function onInputBlurred() {
        if (value !== null) {
            const dateString = dateToString(value, format);

            if (dateString !== inputValue) {
                inputValue = dateString;
            }
        }
    }

    function dateSelected({ detail: event }) {
        if (event.type === 'click' && closeOnSelect) {
            // If this is a mouse click screen X and Y will be set, if not do not close
            if (event.screenX !== 0 && event.screenY !== 0) {
                setExpanded(false);
            }
        } else if (event.type === 'keydown') {
            const key = normalizeKey(event.key);

            if (
                key === Keys.SPACE ||
                key === Keys.ENTER ||
                key === Keys.ESCAPE
            ) {
                setExpanded(false);
            }
        }

        dispatch('select', event);
    }

    function setExpanded(newExpanded) {
        if (newExpanded && disabled) {
            return;
        }

        const inputElementInner = inputElement.querySelector(`input`);

        if (newExpanded === expanded) {
            return;
        }

        if (newExpanded) {
            const wrapperRect = wrapperElement.getBoundingClientRect();
            const inputRect = inputElementInner.getBoundingClientRect();

            position = {
                x: inputRect.left - wrapperRect.left,
                y: inputRect.top + inputRect.height - wrapperRect.top,
            };

            focusValue();
            expanded = newExpanded;
        } else {
            setTimeout(() => {
                inputElementInner.focus();
                expanded = newExpanded;
            }, 0);
        }
    }

    function onInputKeyPress(event) {
        const { key } = event;

        switch (normalizeKey(key)) {
            case Keys.ENTER:
                dispatch('select', event);

                break;
        }
    }
</script>

{#if expanded}
    <div
        class="prom-date-picker__overlay"
        on:click={() => setExpanded(false)}>
    </div>
{/if}

<div bind:this={wrapperElement} class="prom-date-picker__wrapper">
    <div bind:this={inputElement} class="prom-date-picker__input">
        <TextField
            id="{id}-input"
            type="text"
            hint={null}
            {label}
            {autocomplete}
            {disabled}
            {labelHidden}
            {labelPosition}
            {placeholder}
            {hasError}
            {errorMessage}
            bind:value={inputValue}
            on:keyup={onInputKeyPress}
            on:blur={onInputBlurred} 
            on:blur
        />
        <button
            {disabled}
            type="button"
            class="prom-date-picker__picker-button"
            class:prom-date-picker__picker-button--top={labelPosition ===
                'top'}
            on:click={() => setExpanded(true)}>
            <span class="screen-reader"
                >{i18n.openDatePicker}</span>
        </button>
    </div>

    <div
        class="prom-date-picker__popup"
        {style}
        bind:this={tabTrapElement}>
        {#if expanded}
            <div
                role="dialog"
                aria-label={i18n.datePickerTitle}
                aria-modal="true">
                <DatePicker
                    bind:value
                    {firstDayOfWeek}
                    {locale}
                    {i18n}
                    on:select={dateSelected}
                    {selectorStartDate}
                    {highlightedDates} 
                />
            </div>
        {/if}
    </div>
</div>
